import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type Theme = 'light' | 'dark';

function getInitialTheme(): Theme {
  const stored = localStorage.getItem('eraf:theme');
  if (stored === 'light' || stored === 'dark') return stored;
  return 'dark';
}

export function ThemeToggle() {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  const toggle = useCallback(() => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    document.documentElement.setAttribute('data-theme', next);
    localStorage.setItem('eraf:theme', next);
  }, [theme]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle();
      }
    },
    [toggle]
  );

  const isDark = theme === 'dark';
  const label = isDark ? t('appshell.theme.toLight') : t('appshell.theme.toDark');

  return (
    <button
      type="button"
      onClick={toggle}
      onKeyDown={handleKeyDown}
      aria-label={label}
      aria-pressed={isDark}
      title={label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '36px',
        height: '36px',
        borderRadius: 'var(--radius-full)',
        border: '1px solid var(--color-surface-border)',
        background: 'var(--color-surface-muted)',
        color: 'var(--color-text-primary)',
        cursor: 'pointer',
        fontSize: 'var(--font-size-base)',
        transition: 'background 0.15s ease, border-color 0.15s ease',
        flexShrink: 0,
      }}
    >
      {isDark ? '☀' : '🌙'}
    </button>
  );
}
